import { AnimatePresence, motion } from 'framer-motion'
import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { FaChevronRight, FaQuestionCircle } from 'react-icons/fa'
import styled from 'styled-components'
import { TextTooltip } from '@root/components'

interface Props {
  title: string | React.ReactNode
  info?: string
  infoMaxWidth?: number
  openByDefault?: boolean
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  thinBorder?: boolean
  children: React.ReactNode
}

export const ToggleContainer = forwardRef<{ handleContainerOpen: (value: boolean) => void }, Props>(
  (
    {
      title,
      info,
      infoMaxWidth,
      openByDefault = false,
      isOpen: controlledIsOpen,
      onOpenChange,
      thinBorder = false,
      children
    },
    ref
  ) => {
    const isControlled = controlledIsOpen !== undefined
    const [internalIsOpen, setInternalIsOpen] = useState(openByDefault)
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen

    const setOpen = (value: boolean) => {
      if (isControlled) {
        onOpenChange?.(value)
        return
      }

      setInternalIsOpen(value)
    }

    useImperativeHandle(ref, () => ({
      handleContainerOpen: (value: boolean) => {
        setOpen(value)
      }
    }))

    return (
      <StyledToggleContainer $thinBorder={thinBorder}>
        <StyledToggle $isOpen={isOpen} onClick={() => setOpen(!isOpen)} aria-expanded={isOpen}>
          <StyledTitle>
            <span>{title}</span>
            {isOpen && info && (
              <InfoTrigger
                onClick={event => event.stopPropagation()}
                onKeyDown={event => event.stopPropagation()}
              >
                <TextTooltip placement="bottom-start" maxWidth={infoMaxWidth} tooltip={info}>
                  <HelpIcon aria-label={info} />
                </TextTooltip>
              </InfoTrigger>
            )}
          </StyledTitle>
          <StyledToggleIconWrapper animate={isOpen ? 'open' : 'closed'}>
            <FaChevronRight />
          </StyledToggleIconWrapper>
        </StyledToggle>
        <AnimatePresence>
          {isOpen && (
            <StyledContentWrapper key="styled-content-wrapper">{children}</StyledContentWrapper>
          )}
        </AnimatePresence>
      </StyledToggleContainer>
    )
  }
)

const StyledToggleContainer = styled(motion.div)<{ $thinBorder?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme, $thinBorder }) =>
    $thinBorder ? `${theme.borderRadius.md}rem` : '1rem'};
`

const StyledTitle = styled.span`
  flex: 1;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const InfoTrigger = styled.span`
  display: inline-flex;
  align-items: center;
`

const HelpIcon = styled(FaQuestionCircle)`
  display: block;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  cursor: pointer;
`

const StyledToggle = styled.button.attrs({ type: 'button' })<{ $isOpen: boolean }>`
  display: flex;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.md}rem;
  color: inherit;
  font: inherit;
  & > * {
    align-self: center;
  }
`

const StyledContentWrapper = styled(motion.div).attrs({
  variants: {
    init: { opacity: 0, y: -5 },
    open: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 0 }
  },
  initial: 'init',
  animate: 'open',
  exit: 'exit'
})`
  padding: ${({ theme }) => theme.spacing.md}rem;
`

const StyledToggleIconWrapper = styled(motion.div).attrs({
  whileHover: { scale: 1.4 },
  variants: {
    open: { rotateZ: 90 },
    closed: { rotateZ: 0 }
  }
})``
